import supabase from '../utils/supabase';
import { SupabaseError, withErrorHandling } from '../utils/errorHandling';

/**
 * Service for managing user-added skills on the admin side
 * Allows admins to view, filter, and delete skills that users have added via up-skills page
 */

const skillManagementService = {
  /**
   * Get all user profiles with their skills
   * @param {Object} filters - Optional filters
   * @param {string} filters.searchTerm - Search in skill name, category, or user name
   * @param {string} filters.category - Filter by skill category
   * @param {string} filters.userRole - Filter by user role (va, employer)
   * @returns {Promise<Object>} Success status and data
   */
  async getAllUserSkills(filters = {}) {
    return withErrorHandling(async () => {
      console.log('[skillManagementService] getAllUserSkills called with filters:', filters);
      
      let query = supabase
        .from('user_profiles')
        .select('id, full_name, email, role, skills, created_at, updated_at')
        .not('skills', 'is', null)
        .neq('skills', '[]');
      
      // Apply user role filter if provided
      if (filters.userRole && filters.userRole !== 'all') {
        query = query.eq('role', filters.userRole);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      console.log('[skillManagementService] Query result:', { data_length: data?.length, error });

      if (error) throw new SupabaseError(error.message, error, { table: 'user_profiles' });

      // Process and flatten skills data with user context
      const processedData = [];
      
      if (!Array.isArray(data)) {
        console.warn('[skillManagementService] Data is not an array:', typeof data);
        return processedData;
      }
      
      console.log('[skillManagementService] Processing', data.length, 'users');
      
      data.forEach((user, userIdx) => {
        const skills = Array.isArray(user.skills) ? user.skills : [];
        console.log(`[skillManagementService] User ${userIdx} (${user.full_name}): ${skills.length} skill items`);
        
        skills.forEach((skill, index) => {
          // Handle different skill data formats
          let skillName = '';
          let skillId = null;
          let skillRating = null;
          let skillExperience = '';
          let skillType = 'custom';
          
          // Case 1: Skill is an object (with id, name, type, etc.)
          if (typeof skill === 'object' && skill !== null && skill.name) {
            skillName = skill.name;
            skillId = skill.id || null;
            skillRating = skill.rating || null;
            skillExperience = skill.experience || '';
            skillType = skill.type || 'custom';
          }
          // Case 2: Skill is a string (simple or with escaped quotes)
          else if (typeof skill === 'string') {
            skillName = skill;
          }
          // Case 3: Skip null/undefined
          else {
            return;
          }
          
          // Clean the skill name:
          // 1. Trim whitespace
          // 2. Remove outer quotes (both escaped \" and regular ")
          // 3. Remove any remaining escape characters
          let cleanSkillName = skillName.trim();
          
          // Remove escaped quotes: \" becomes "
          cleanSkillName = cleanSkillName.replace(/\\"/g, '"');
          
          // Remove outer quotes (both single and double)
          cleanSkillName = cleanSkillName.replace(/^["']|["']$/g, '');
          
          // Remove remaining backslashes
          cleanSkillName = cleanSkillName.replace(/\\/g, '').trim();
          
          console.log(`[skillManagementService] Skill ${index}: raw="${skill}", cleaned="${cleanSkillName}"`);
          
          // Skip empty strings and placeholder values after cleaning
          if (!cleanSkillName || cleanSkillName === '""' || cleanSkillName === "''") {
            console.log(`[skillManagementService] Skipping empty skill after cleaning`);
            return;
          }
          
          // Handle comma-separated skills (if a single skill string contains commas, split it)
          // e.g., "Customer Support, Admin Assistant" should become 2 entries
          const skillsList = cleanSkillName.split(',').map(s => s.trim()).filter(s => s);
          
          skillsList.forEach(finalSkillName => {
            // Since skills are stored as various formats, categorize by skill type
            // or default to 'General' for simple string skills
            const skillCategory = skillType === 'upskills' ? 'Professional Development' : 'General';
            
            // Apply search filter
            if (filters.searchTerm) {
              const searchLower = filters.searchTerm.toLowerCase();
              const matchesSearch = 
                finalSkillName.toLowerCase().includes(searchLower) ||
                skillCategory.toLowerCase().includes(searchLower) ||
                user.full_name.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower);
              
              if (!matchesSearch) return;
            }
            
            // Apply category filter
            if (filters.category && filters.category !== 'all') {
              if (skillCategory.toLowerCase() !== filters.category.toLowerCase()) return;
            }
            
            processedData.push({
              userId: user.id,
              userName: user.full_name,
              userEmail: user.email,
              userRole: user.role,
              skillName: finalSkillName,
              skillCategory,
              skillRating,
              skillExperience,
              skillType,
              skillId,
              skillIndex: index,
              addedAt: user.updated_at,
              originalSkillObject: skill
            });
          });
        });
      });

      console.log('[skillManagementService] Processed', processedData.length, 'skill entries from', data.length, 'users');
      
      return processedData;
    }, { table: 'user_profiles', action: 'fetch user skills' });
  },

  /**
   * Get aggregated statistics about user skills
   * @returns {Promise<Object>} Statistics data
   */
  async getSkillsStatistics() {
    return withErrorHandling(async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('skills, role')
        .not('skills', 'is', null)
        .neq('skills', '[]');

      if (error) throw new SupabaseError(error.message, error, { table: 'user_profiles' });

      const stats = {
        totalUsers: 0,
        totalSkills: 0,
        skillsByCategory: {},
        skillsByRole: { va: 0, employer: 0, other: 0 },
        customSkills: 0,
        upskillsFormatSkills: 0,
        topSkills: {},
        topSkillsList: []
      };

      if (!Array.isArray(data)) {
        return stats;
      }

      data.forEach(user => {
        const skills = Array.isArray(user.skills) ? user.skills : [];
        if (skills.length > 0) {
          stats.totalUsers++;
          
          // Don't count raw array length yet - we'll count actual skills after cleaning/splitting
          let userActualSkillCount = 0;

          // Count by role
          let userSkillsByRole = 0;

          skills.forEach(skill => {
            // Handle different skill formats
            let skillName = '';
            let skillType = 'custom';
            
            // Case 1: Object with name property
            if (typeof skill === 'object' && skill !== null && skill.name) {
              skillName = skill.name;
              skillType = skill.type || 'custom';
            }
            // Case 2: Simple string
            else if (typeof skill === 'string') {
              skillName = skill;
            }
            // Skip if neither
            else {
              return;
            }
            
            // Clean the skill name
            let cleanedName = skillName.trim();
            cleanedName = cleanedName.replace(/\\"/g, '"');
            cleanedName = cleanedName.replace(/^["']|["']$/g, '');
            cleanedName = cleanedName.replace(/\\/g, '').trim();
            
            // Skip empty or placeholder values
            if (!cleanedName || cleanedName === '""' || cleanedName === "''") {
              return;
            }
            
            // Split by comma to handle comma-separated skills
            const skillsList = cleanedName.split(',').map(s => s.trim()).filter(s => s);
            
            skillsList.forEach(finalSkillName => {
              userActualSkillCount++;
              userSkillsByRole++;
              
              const skillCategory = skillType === 'upskills' ? 'Professional Development' : 'General';

              // Category count
              stats.skillsByCategory[skillCategory] = (stats.skillsByCategory[skillCategory] || 0) + 1;

              // Track skill type distribution
              if (skillType === 'upskills') {
                stats.upskillsFormatSkills++;
              } else {
                stats.customSkills++;
              }

              // Top skills
              stats.topSkills[finalSkillName] = (stats.topSkills[finalSkillName] || 0) + 1;
            });
          });
          
          // Now add the actual count of skills (after splitting)
          stats.totalSkills += userActualSkillCount;
          
          if (user.role === 'va') {
            stats.skillsByRole.va += userSkillsByRole;
          } else if (user.role === 'employer') {
            stats.skillsByRole.employer += userSkillsByRole;
          } else {
            stats.skillsByRole.other += userSkillsByRole;
          }
        }
      });

      // Sort top skills
      stats.topSkillsList = Object.entries(stats.topSkills)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([name, count]) => ({ name, count }));

      return stats;
    }, { table: 'user_profiles', action: 'fetch skills statistics' });
  },

  /**
   * Delete a skill from a user's profile by skill name
   * Uses a database function with SECURITY DEFINER to bypass RLS for admin operations
   * @param {string} userId - User ID
   * @param {string} skillName - Name of the skill to delete
   * @returns {Promise<Object>} Success status
   */
  async deleteUserSkillByName(userId, skillName) {
    return withErrorHandling(async () => {
      console.log('[skillManagementService] deleteUserSkillByName:', { userId, skillName });
      
      // Use the database function that runs with SECURITY DEFINER
      const { data, error } = await supabase.rpc('admin_delete_skill_by_name', {
        target_user_id: userId,
        skill_name: skillName
      });

      console.log('[skillManagementService] RPC result:', { data, error });

      if (error) {
        console.error('[skillManagementService] RPC error:', error);
        throw new SupabaseError(error.message, error, { table: 'user_profiles' });
      }

      // The function returns a JSON object with success, message, etc.
      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to delete skill');
      }

      console.log('[skillManagementService] Skill deleted successfully:', data);
      return { 
        message: data?.message || `Skill "${skillName}" deleted successfully`,
        previousCount: data?.previous_count,
        newCount: data?.new_count
      };
    }, { table: 'user_profiles', action: 'delete user skill by name' });
  },

  /**
   * Delete a skill from a user's profile by index (legacy method)
   * NOTE: This method is unreliable for comma-separated skills. Use deleteUserSkillByName instead.
   * @param {string} userId - User ID
   * @param {number} skillIndex - Index of the skill in the skills array
   * @param {string} skillId - Optional skill ID for verification
   * @returns {Promise<Object>} Success status
   * @deprecated Use deleteUserSkillByName instead
   */
  async deleteUserSkill(userId, skillIndex, skillId = null) {
    return withErrorHandling(async () => {
      // First, fetch the user's current skills
      const { data: userData, error: fetchError } = await supabase
        .from('user_profiles')
        .select('skills')
        .eq('id', userId)
        .single();

      if (fetchError) throw new SupabaseError(fetchError.message, fetchError, { table: 'user_profiles' });

      if (!userData || !Array.isArray(userData.skills)) {
        throw new Error('User has no skills or invalid skills format');
      }

      // Verify the skill exists at the given index
      if (skillIndex < 0 || skillIndex >= userData.skills.length) {
        throw new Error('Invalid skill index');
      }

      // Optional: Verify skill ID if provided
      if (skillId && userData.skills[skillIndex].id !== skillId) {
        throw new Error('Skill ID mismatch - skill may have been modified');
      }

      // Remove the skill from the array
      const updatedSkills = [
        ...userData.skills.slice(0, skillIndex),
        ...userData.skills.slice(skillIndex + 1)
      ];

      // Update the user's profile
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ skills: updatedSkills, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateError) throw new SupabaseError(updateError.message, updateError, { table: 'user_profiles' });

      return { message: 'Skill deleted successfully' };
    }, { table: 'user_profiles', action: 'delete user skill' });
  },

  /**
   * Bulk delete skills matching a specific name across all users
   * Uses a database function with SECURITY DEFINER to bypass RLS for admin operations
   * @param {string} skillName - Skill name to delete
   * @returns {Promise<Object>} Success status with count of deleted skills
   */
  async bulkDeleteSkillByName(skillName) {
    return withErrorHandling(async () => {
      console.log('[skillManagementService] bulkDeleteSkillByName:', { skillName });
      
      // Use the database function that runs with SECURITY DEFINER
      const { data, error } = await supabase.rpc('admin_bulk_delete_skill_by_name', {
        skill_name: skillName
      });

      console.log('[skillManagementService] Bulk delete RPC result:', { data, error });

      if (error) {
        console.error('[skillManagementService] Bulk delete RPC error:', error);
        throw new SupabaseError(error.message, error, { table: 'user_profiles' });
      }

      // The function returns a JSON object with success, message, etc.
      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to bulk delete skill');
      }

      console.log('[skillManagementService] Bulk delete successful:', data);
      return { 
        message: data?.message || `Skill "${skillName}" deleted from all users`,
        deletedCount: data?.deleted_count || 0,
        affectedUsers: data?.users_affected || 0
      };
    }, { table: 'user_profiles', action: 'bulk delete skill' });
  },

  /**
   * Get all skills with their user context for category extraction
   * @returns {Promise<Array>} Array of user profiles with skills
   */
  async getAllSkillsWithCategories() {
    return withErrorHandling(async () => {
      console.log('[skillManagementService] getAllSkillsWithCategories called');
      
      const query = supabase
        .from('user_profiles')
        .select('id, full_name, email, role, skills, created_at, updated_at')
        .not('skills', 'is', null)
        .neq('skills', '[]');

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) throw new SupabaseError(error.message, error, { table: 'user_profiles' });

      return data || [];
    }, { table: 'user_profiles', action: 'fetch all skills' });
  },

  /**
   * Get unique categories from all user skills
   * @returns {Promise<Object>} List of categories
   */
  async getSkillCategories() {
    return withErrorHandling(async () => {
      console.log('[skillManagementService] getSkillCategories called');
      
      // Get all skills first
      const skillsResponse = await this.getAllSkillsWithCategories();
      
      if (!skillsResponse.success || !Array.isArray(skillsResponse.data)) {
        console.warn('[skillManagementService] Could not fetch skills for categories');
        return ['General']; // Fallback
      }
      
      // Extract unique categories from all skills
      const categoriesSet = new Set();
      
      skillsResponse.data.forEach(user => {
        const skills = Array.isArray(user.skills) ? user.skills : [];
        skills.forEach(skill => {
          let category = 'General'; // Default category
          
          // If skill is an object with a category/type field
          if (typeof skill === 'object' && skill !== null) {
            if (skill.category) category = skill.category;
            else if (skill.type && skill.type !== 'custom' && skill.type !== 'upskills') {
              category = skill.type;
            }
          }
          
          categoriesSet.add(category);
        });
      });
      
      const categories = Array.from(categoriesSet).sort();
      console.log('[skillManagementService] Extracted categories:', categories);
      
      return categories;
    }, { table: 'user_profiles', action: 'fetch skill categories' });
  }
};

export default skillManagementService;
